import { NavigableButton } from '@carvana/ds-button';
import { icon } from '@carvana/ds-styles';
import { InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import abbreviateMileage from '@carvana/merch-tile/utils/abbreviateMileage';
import { AlertCircle } from 'lucide-react';
import OrderState from '~/constants/common/OrderState';
import { PurchaseEventPayload } from '~/native/common/contracts';
import { useSourcedHandleNativePurchaseOnClick } from '~/native/common/hooks';
import {
  getNativeVdpPayload,
  nativeMyCarsPayload,
  nativeOpdPayload
} from '~/native/common/utils';
import {
  BodyText,
  Card,
  ContentSection,
  Headline,
  TaskWarning,
  VehicleInfo,
  VehicleSection,
  VehicleSubText,
  VehicleTitle
} from './OrderStatusCard.styles';
import { OrderStatusCardConfig } from '../utils';

type OwnProps = {
  config: OrderStatusCardConfig;
  orderState: OrderState;
  vehicle: {
    imageUrl: string;
    inventoryType: InventoryTypeKey;
    make: string;
    mileage: number;
    model: string;
    stockNumber: number;
    trim: string;
    vehicleId: number;
    year: number;
  };
};

export default function OrderStatusCard({
  config,
  orderState,
  vehicle
}: OwnProps) {
  const handleNativePurchaseOnClick = useSourcedHandleNativePurchaseOnClick();

  const {
    bodyText,
    ctaDescription,
    ctaLink,
    ctaText,
    headlineText,
    openVerificationsCount
  } = config;
  const {
    imageUrl,
    inventoryType,
    make,
    mileage,
    model,
    stockNumber,
    trim,
    vehicleId,
    year
  } = vehicle;

  const isContinuePurchase =
    orderState === OrderState.InProgress ||
    orderState === OrderState.InProgressWithTradeIn;

  let nativePayload = '';
  switch (orderState) {
    case OrderState.OrderPlaced:
      nativePayload = nativeOpdPayload;
      break;
    case OrderState.RecentlyPurchased:
      nativePayload = nativeMyCarsPayload;
      break;
    case OrderState.Reserved:
      nativePayload = vehicleId ? getNativeVdpPayload(vehicleId) : '';
      break;
  }

  const nativePurchaseEventPayload: PurchaseEventPayload = {
    inventoryType,
    make,
    model,
    stockNumber,
    trim,
    vehicleId,
    vehicleImage: imageUrl,
    year
  };

  const ctaProps = {
    'data-analytics-attributes': JSON.stringify({
      description: ctaDescription,
      vehicleId
    }),
    'data-analytics-id': 'Home feed card',
    'data-native-included': !isContinuePurchase,
    'data-native-payload': nativePayload,
    href: ctaLink,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isContinuePurchase) {
        handleNativePurchaseOnClick(event, nativePurchaseEventPayload);
      }
    }
  };

  return (
    <Card>
      <ContentSection>
        <Headline style={{ textWrap: 'balance' } as React.CSSProperties}>
          {headlineText}
        </Headline>

        {openVerificationsCount != null && (
          <TaskWarning>
            <AlertCircle color={icon.warning} size={24} />
            <span className="text-text-strong t-heading-sm">
              You have{' '}
              {openVerificationsCount === 1
                ? '1 task'
                : `${openVerificationsCount} tasks`}{' '}
              to complete.
            </span>
          </TaskWarning>
        )}

        {bodyText && <BodyText>{bodyText}</BodyText>}
      </ContentSection>

      <VehicleSection>
        <VehicleInfo>
          <VehicleTitle>
            {year} {make} {model}
          </VehicleTitle>
          <VehicleSubText>
            <span>{trim}</span>
            {mileage > 0 && (
              <>
                <span
                  aria-hidden="true"
                  className="inline-block size-[3px] rounded-full bg-icon-strong"
                />
                <span>{abbreviateMileage(mileage)} miles</span>
              </>
            )}
          </VehicleSubText>
        </VehicleInfo>

        <NavigableButton
          {...ctaProps}
          className="sm:w-auto sm:self-start"
          size="md"
          variant="primary"
        >
          {ctaText}
        </NavigableButton>
      </VehicleSection>
    </Card>
  );
}
